/**
 * Tiny page-chrome primitives kept OUT of the giant `pages.tsx` module.
 *
 * `__root.tsx` renders {@link NotFoundPage} for the global 404
 * (`notFoundComponent`), so whatever module it lives in is pulled into the
 * eager root/shell bundle. Co-locating it in `pages.tsx` dragged that entire
 * module - and, through it, the full content catalog with every lesson body -
 * onto the critical path. Living here, the 404 ships only this handful of lines
 * plus the i18n helpers it already needs.
 *
 * {@link PageIntro} is the shared hero/header block every page renders, so it
 * lives here too and is re-exported by `pages.tsx`; this keeps both primitives
 * in one small, body-free module.
 *
 * {@link RouteErrorPage} is the router's `defaultErrorComponent`: TanStack
 * Router catches render/loader errors in its own boundary, so they never reach
 * `window.onerror` (PostHog exception autocapture). It reports them
 * explicitly, once per error, and offers a retry.
 */
import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { localizedPath, useTranslation } from '../lib/i18n'
import { trackEvent } from '../lib/analytics'
import { captureAnalyticsException } from '../lib/analytics/posthog-client'
import { referrerKind, sanitizePathForAnalytics } from '../lib/analytics/consent-policy'

export function PageIntro({
  eyebrow,
  title,
  text,
  actions,
}: {
  eyebrow: string
  title: string
  text: string
  actions?: ReactNode
}) {
  return (
    <header className="page-intro" data-hero-sentinel>
      <div className="page-intro-body">
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{text}</p>
        {actions ? (
          <div className="hero-actions page-intro-actions">{actions}</div>
        ) : null}
      </div>
    </header>
  )
}

export function NotFoundPage({ label }: { label?: string }) {
  const { t, locale } = useTranslation()
  useEffect(() => {
    trackEvent('page_not_found', {
      path: sanitizePathForAnalytics(window.location.pathname),
      kind: label ? 'content' : 'route',
      referrer_kind: referrerKind(document.referrer, window.location.hostname),
    })
  }, [label])
  return (
    <main className="page-shell narrow">
      <PageIntro
        eyebrow="404"
        title={label ?? t('notFound.label')}
        text={t('notFound.body')}
      />
      <a className="primary-button" href={localizedPath('home', locale)}>
        {t('notFound.home')}
      </a>
    </main>
  )
}

export function RouteErrorPage({
  error,
  reset,
}: {
  error: unknown
  reset?: () => void
}) {
  const { t, locale } = useTranslation()
  const reported = useRef<unknown>(null)
  useEffect(() => {
    if (reported.current === error) return
    reported.current = error
    captureAnalyticsException(error, {
      boundary: 'route',
      path: sanitizePathForAnalytics(window.location.pathname),
    })
  }, [error])
  return (
    <main className="page-shell narrow" role="alert">
      <PageIntro
        eyebrow="Error"
        title={t('routeError.title')}
        text={t('routeError.body')}
      />
      <div className="hero-actions page-intro-actions">
        {reset ? (
          <button type="button" className="primary-button" onClick={reset}>
            {t('routeError.retry')}
          </button>
        ) : null}
        <a className="ghost-button" href={localizedPath('home', locale)}>
          {t('routeError.home')}
        </a>
      </div>
    </main>
  )
}
